/*
 * Copyright (c) Secureroo PTY LTD 2022
 */

import Debugger from './Debugger';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  OAuthProvider,
  sendEmailVerification,
  onAuthStateChanged,
} from 'firebase/auth';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import {
  getFirestore,
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  setDoc,
  query,
  where,
  serverTimestamp,
  or,
  Timestamp,
  deleteField,
  arrayUnion,
  onSnapshot,
} from 'firebase/firestore';
import { getStorage, ref, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import { isValidPhoneNumber } from 'libphonenumber-js';

const FBConfig = {
  apiKey: import.meta.env.VITE_apiKey,
  authDomain: import.meta.env.VITE_authDomain,
  projectId: import.meta.env.VITE_projectId,
  storageBucket: import.meta.env.VITE_storageBucket,
  messagingSenderId: import.meta.env.VITE_messagingSenderId,
  appId: import.meta.env.VITE_appId,
  measurementId: import.meta.env.VITE_measurementId,
};

const FBApp = initializeApp(FBConfig);
const FBAuth = getAuth(FBApp);
const FBDatabase = getFirestore(FBApp);
const FBStorage = getStorage(FBApp);
const FBFunctions = getFunctions(FBApp); 
// const FBFunctions = getFunctions(FBApp, "australia-southeast1"); 
// connectFunctionsEmulator(FBFunctions, '127.0.0.1', 5001);
const previousDataTypes = [];
const previousTools = [];
const previousAccountingTools = [];
const previousSalesTools = [];
const dataStorageLocations = {};
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('https://www.googleapis.com/auth/userinfo.email');
googleProvider.addScope('https://www.googleapis.com/auth/userinfo.profile');
const microsoftProvider = new OAuthProvider('microsoft.com');
var companyDomain;
const abnWeights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
const acnWeights = [8, 7, 6, 5, 4, 3, 2, 1];

export default class OnboardingCommon {
  /**
   * The constructor function initializes various properties and checks the URL for a specific pattern.
   */
  constructor() {
    this.FBApp = FBApp;
    this.FBAuth = FBAuth;
    this.FBDatabase = FBDatabase;
    this.FBStorage = FBStorage;
    this.FBFunctions = FBFunctions;
    this.isDebug = true; // Set to true to enable console logging
    this.demo = false; // Set to false to disable the prod/demo warning message
    this.debug = new Debugger(this.isDebug, this);
    if (
      (!this.isDebug && this.demo) ||
      (!this.isDebug && !this.demo) ||
      (this.isDebug && this.demo)
    ) {
      this.consoleWarning();
    }

    if (!this.isDebug) {
      console.log = function () {};
    }

    let originalLog = console.log;

    let url = window.location.toString();
    if (url.includes('#!')) {
      const pathAfterHashBang = url.split('#!')[1];
      const pageName = pathAfterHashBang.split('/')[1];
      console.log(
        'Path component after #!: ',
        this.getCapitalised(pageName.replaceAll('/', '').replaceAll('-', ' '))
      );
    } else {
      console.log('URL does not contain the #! pattern.');
    }
  }

  async login(email, password) {
    signInWithEmailAndPassword(this.FBAuth, email, password);
  }

  /**
   * The function `isOnboardingInProgress` checks if there is a signed-in user and redirects to the
   * onboarding page if not.
   * @returns a Promise.
   */
  async isOnboardingInProgress() {
    return new Promise(async (resolve, reject) => {
      return await this.checkForSignedInUser()
        .then(async (response) => {
          console.log(response);
          console.log(localStorage.getItem('userID'));

          if (
            !localStorage.getItem('userID') ||
            localStorage.getItem('userID') === null ||
            localStorage.getItem('userID') === undefined ||
            localStorage.getItem('userID') === '' ||
            response.status != 201
          ) {
            console.log('No userID found, redirecting to onboarding');
            setTimeout(() => {
              window.changePage('/auth/');
              return reject({ status: 500, message: response.message });
            }, 1);
          } else {
            console.log('userID found, continuing');
            return resolve({ status: 201, message: response.message });
          }
        })
        .catch((error) => {
          console.log(error);
          setTimeout(() => {
            return reject({ status: 500, message: error.message });
          }, 1);
        });
    });
  }

  /**
   * The function `registerUserWithEmail` is an asynchronous function that takes in parameters for user
   * information (first name, last name, email, password, and phone number) and returns a promise. It
   * checks if all the required fields are filled, if the password is at least 8 characters long, and
   * if the phone number is valid. If all the conditions are met, it checks if the email is disposable
   * and creates a user account using the provided email and password. It then writes the user
   * information to a Firestore document and resolves the promise with a success message and the user
   * ID. If any of the conditions are not met, it rejects the promise with an error message indicating
   * the specific requirement that was not met.
   * @param firstName - The first name of the user.
   * @param lastName - The `lastName` parameter is a string that represents the last name of the user.
   * @param email - The `email` parameter is the email address of the user that is being registered.
   * @param password - The `password` parameter is a string that represents the user's password for
   * their account.
   * @param phoneNumber - The `phoneNumber` parameter is a string that represents the user's phone
   * number.
   * @returns The function `registerUserWithEmail` returns a Promise.
   */
  async registerUserWithEmail(userInputData = {}) {
    return new Promise(async (resolve, reject) => {
      const firstName = userInputData.firstName || '';
      const lastName = userInputData.lastName || '';
      const email = userInputData.email || '';
      const password = userInputData.password || '';
      const phoneNumber = userInputData.phoneNumber || '';
      const isValidNumber = isValidPhoneNumber(phoneNumber, 'AU');
      console.log(firstName, lastName, email, password, isValidNumber);
      if (
        firstName.length !== 0 &&
        lastName.length !== 0 &&
        email.length !== 0 &&
        password.length !== 0 &&
        password.length >= 8 &&
        isValidNumber
      ) {
        const isDisposable = await this.checkForDisposableEmail(email);
        if (isDisposable !== true) {
          return await createUserWithEmailAndPassword(this.FBAuth, email, password)
            .then(async (userCredential) => {
              console.log(userCredential);
              // Signed in
              const user = userCredential.user;
              console.log(user);
              return await setDoc(
                doc(this.FBDatabase, 'users', user.uid),
                {
                  biometricsEnabled: 'false',
                  currentReadTime: '0',
                  email: user.email,
                  firstName: firstName,
                  lastName: lastName,
                  phoneNumber: phoneNumber.toString(),
                  notificationCount: '0',
                  roles: {
                    owner: true,
                  },
                  onboardingStage: 'createAccount',
                  totalCurrentTime: '0',
                  totalTimeEver: '0',
                  userID: user.uid,
                  userType: 'owner',
                  welcomeOverlayCompleted: false,
                },
                { merge: true }
              )
                .then(async () => {
                  return await setPersistence(this.FBAuth, browserLocalPersistence).then(
                    async () => {
                      return await signInWithEmailAndPassword(this.FBAuth, email, password)
                        .then(async (userCredentialSignIn) => {
                          return await sendEmailVerification(this.FBAuth.currentUser)
                            .then(() => {
                              let emailDomain = userCredentialSignIn.user.email.split('@')[1];
                              if (
                                emailDomain !== 'gmail.com' &&
                                emailDomain !== 'yahoo.com' &&
                                emailDomain !== 'hotmail.com' &&
                                emailDomain !== 'outlook.com' &&
                                emailDomain !== 'protonmail.com' &&
                                emailDomain !== 'icloud.com'
                              ) {
                                console.log(
                                  'Email domain is not a standard free email. Email domain: ' +
                                    emailDomain
                                );
                                companyDomain = emailDomain;
                                return resolve({
                                  status: 201,
                                  message: 'Account created successfully and user doc written.',
                                  userID: userCredentialSignIn.user.uid,
                                  emailDomain: emailDomain,
                                  userObject: userCredentialSignIn,
                                });
                              } else {
                                console.log(
                                  'Email domain is a standard free email. Email domain: ' +
                                    emailDomain
                                );
                                companyDomain = '';
                                return resolve({
                                  status: 201,
                                  message: 'Account created successfully and user doc written.',
                                  userID: userCredentialSignIn.user.uid,
                                  userObject: userCredentialSignIn,
                                });
                              }
                            })
                            .catch((error) => {
                              console.log(error);
                              return reject({ status: error.code, message: error.message });
                            });
                        })
                        .catch(async (error) => {
                          console.log(error);
                          return reject({ status: error.code, message: error.message });
                        });
                    }
                  );
                })
                .catch(async (error) => {
                  console.log(error);
                  return reject({ status: error.code, message: error.message });
                });
            })
            .catch(async (error) => {
              if (error.code === 'auth/email-already-in-use') {
                return await setPersistence(FBAuth, browserLocalPersistence).then(async () => {
                  return await signInWithEmailAndPassword(this.FBAuth, email, password)
                    .then(async (userCredentialSignIn) => {
                      return await sendEmailVerification(this.FBAuth.currentUser)
                        .then(() => {
                          let emailDomain = userCredentialSignIn.user.email.split('@')[1];
                          if (
                            emailDomain !== 'gmail.com' &&
                            emailDomain !== 'yahoo.com' &&
                            emailDomain !== 'hotmail.com' &&
                            emailDomain !== 'outlook.com' &&
                            emailDomain !== 'protonmail.com' &&
                            emailDomain !== 'icloud.com'
                          ) {
                            console.log(
                              'Email domain is not a standard free email. Email domain: ' +
                                emailDomain
                            );
                            companyDomain = emailDomain;
                            return resolve({
                              status: 201,
                              message: 'Account created successfully and user doc written.',
                              userID: userCredentialSignIn.user.uid,
                              emailDomain: emailDomain,
                              userObject: userCredentialSignIn,
                            });
                          } else {
                            console.log(
                              'Email domain is a standard free email. Email domain: ' + emailDomain
                            );
                            companyDomain = '';
                            return resolve({
                              status: 201,
                              message: 'Account created successfully and user doc written.',
                              userID: userCredentialSignIn.user.uid,
                              userObject: userCredentialSignIn,
                            });
                          }
                        })
                        .catch((error) => {
                          console.log(error);
                          return reject({ status: error.code, message: error.message });
                        });
                    })
                    .catch(async (error) => {
                      console.log(error);
                      return reject({ status: error.code, message: error.message });
                    });
                });
              } else {
                return reject({ status: error.code, message: error.message });
              }
            });
        } else {
          console.log('Email is disposable. Not creating account.');
          return reject({ status: 500, message: 'Email is disposable.' });
        }
      } else {
        console.log('An error occurred');
        if (firstName.length === 0) {
          return reject({ status: 500, message: 'First name is required.' });
        }
        if (lastName.length === 0) {
          return reject({ status: 500, message: 'Last name is required.' });
        }
        if (email.length === 0) {
          return reject({ status: 500, message: 'Email is required.' });
        }
        if (password.length === 0) {
          return reject({ status: 500, message: 'Password is required.' });
        }
        if (password.length > 0 && password.length < 8) {
          return reject({ status: 500, message: 'Password must be at least 8 characters.' });
        }
        if (phoneNumber.length === 0) {
          return reject({ status: 500, message: 'Phone number is required.' });
        }
        if (!isValidNumber) {
          return reject({ status: 500, message: 'Phone number is invalid.' });
        }
      }
    });
  }

  /**
   * The function `registerUserWithGoogle` is an asynchronous function that registers a user using
   * Google authentication and creates a user document in a Firestore database.
   * @returns a Promise.
   */
  async registerUserWithGoogle() {
    return new Promise(async (resolve, reject) => {
      return await signInWithPopup(this.FBAuth, googleProvider)
        .then(async (userCredential) => {
          console.log(userCredential);
          const userEmail = userCredential.user.email;
          console.log('Google User Email: ' + userEmail);
          const emailDomain = userEmail.split('@')[1];
          const firstName = userCredential.user?.displayName?.split(' ')[0] || '';
          const lastName = userCredential.user?.displayName?.split(' ')[1] || '';
          const phoneNumber = userCredential.user.phoneNumber;
          if (emailDomain !== 'gmail.com') {
            companyDomain = emailDomain;
          } else {
            companyDomain = '';
          }
          console.log(companyDomain);
          return await setDoc(
            doc(this.FBDatabase, 'users', userCredential.user.uid),
            {
              biometricsEnabled: 'false',
              currentReadTime: '0',
              email: userCredential.user.email,
              firstName: firstName,
              lastName: lastName,
              phoneNumber: phoneNumber,
              notificationCount: '0',
              roles: {
                owner: true,
              },
              onboardingStage: 'createAccount',
              totalCurrentTime: '0',
              totalTimeEver: '0',
              userID: userCredential.user.uid,
              userType: 'owner',
              welcomeOverlayCompleted: false,
            },
            { merge: true }
          )
            .then(async () => {
              return await sendEmailVerification(this.FBAuth.currentUser)
                .then(() => {
                  return resolve({
                    status: 201,
                    message: 'Account created successfully and user doc written.',
                    userID: userCredential.user.uid,
                    userObject: userCredential,
                  });
                })
                .catch((error) => {
                  console.log(error);
                  return reject({ status: error.code, message: error.message });
                });
            })
            .catch((error) => {
              return reject({ status: error.code, message: error.message });
            });
        })
        .catch((error) => {
          console.log(error);
          return reject({ status: error.code, message: error.message });
        });
    });
  }

  /**
   * The function `registerUserWithMicrosoft` registers a user with Microsoft authentication and
   * creates a user document in a Firebase database.
   * @returns a Promise.
   */
  async registerUserWithMicrosoft() {
    return new Promise(async (resolve, reject) => {
      return await signInWithPopup(this.FBAuth, microsoftProvider)
        .then(async (userCredential) => {
          await userCredential;
          console.log(userCredential);
          const userEmail = userCredential.user.email;
          console.log('Microsoft User Email: ' + userEmail);
          const emailDomain = userEmail.split('@')[1];
          const firstName = userCredential.user?.displayName?.split(' ')[0] || '';
          const lastName = userCredential.user?.displayName?.split(' ')[1] || '';
          const phoneNumber = userCredential.user?.phoneNumber || '';
          if (emailDomain !== 'outlook.com' || emailDomain!== 'gmail.com') {
            companyDomain = emailDomain;
          } else {
            companyDomain = '';
          }
          console.log(companyDomain);
          return await setDoc(
            doc(this.FBDatabase, 'users', userCredential.user.uid),
            {
              biometricsEnabled: 'false',
              currentReadTime: '0',
              email: userCredential.user.email,
              firstName: firstName,
              lastName: lastName,
              phoneNumber: phoneNumber,
              notificationCount: '0',
              roles: {
                owner: true,
              },
              onboardingStage: 'createAccount',
              totalCurrentTime: '0',
              totalTimeEver: '0',
              userID: userCredential.user.uid,
              userType: 'owner',
              welcomeOverlayCompleted: false,
            },
            { merge: true }
          )
            .then(async () => {
              return await sendEmailVerification(this.FBAuth.currentUser)
                .then(() => {
                  return resolve({
                    status: 201,
                    message: 'Account created successfully and user doc written.',
                    userID: userCredential.user.uid,
                    userObject: userCredential,
                  });
                })
                .catch((error) => {
                  console.log(error);
                  return reject({ status: error.code, message: error.message });
                });
            })
            .catch((error) => {
              return reject({ status: error.code, message: error.message });
            });
        })
        .catch((error) => {
          console.log(error);
          return reject({ status: error.code, message: error.message });
        });
    });
  }

  /**
   * The function `checkCompanyData` checks if all required company data is provided and validates the
   * ABN or ACN before creating an automated onboarding document.
   * @param [companyData] - The `companyData` parameter is an object that contains the following
   * properties:
   * @returns a Promise.
   */
  async checkCompanyData(companyData = {}) {
    return new Promise(async (resolve, reject) => {
      const companyName = companyData.companyName || '';
      const companyWebsite = companyData.companyWebsite || '';
      const companyIndustry = companyData.companyIndustry || '';
      const companyLocation = companyData.companyLocation || '';
      const rawABNInput = companyData.abn.toString().replace(/[^a-z\d]/gi, '') || '';
      const abn = rawABNInput.toString().replace(/\s/g, '') || '';
      if (
        companyName.length !== 0 &&
        companyWebsite.length !== 0 &&
        companyIndustry.length !== 0 &&
        companyLocation.length !== 0 &&
        abn.length !== 0
      ) {
        // if (abn.length === 11) {
        //   let validABN = await this.isValidABN(abn);
        //   if (validABN) {
        //     return resolve(await this.createAutomatedOnboardingDoc(companyData));
        //   } else {
        //     return reject({ status: 500, message: 'ABN is invalid.' });
        //   }
        // } else if (abn.length === 9) {
        //   let validACN = await this.isValidACN(abn);
        //   if (validACN) {
        //     return resolve(await this.createAutomatedOnboardingDoc(companyData));
        //   } else {
        //     return reject({ status: 500, message: 'ACN is invalid.' });
        //   }
        // } else {
        //   return reject({ status: 500, message: 'ABN/ACN is invalid length.' });
        // }
        return resolve(await this.createAutomatedOnboardingDoc(companyData));
      } else {
        if (companyName.length === 0) {
          return reject({ status: 500, message: 'Company name is required.' });
        }
        if (companyWebsite.length === 0) {
          return reject({ status: 500, message: 'Company website is required.' });
        }
        if (companyIndustry.length === 0) {
          return reject({ status: 500, message: 'Company industry is required.' });
        }
        if (companyLocation.length === 0) {
          return reject({ status: 500, message: 'Company location is required.' });
        }
        if (abn.length === 0) {
          return reject({ status: 500, message: 'ABN is required.' });
        }
      }
    });
  }

  /**
   * The function creates an automated onboarding document for a company and updates the user's
   * onboarding stage.
   * @param companyData - The `companyData` parameter is an object that contains the following
   * properties:
   * @returns a Promise.
   */
  async createAutomatedOnboardingDoc(companyData) {
    return new Promise(async (resolve, reject) => {
      let createDoc = await addDoc(collection(this.FBDatabase, 'pa_automated_onboarding'), {
        abn: companyData.abn,
        companyDomain: companyData.companyWebsite,
        companyName: companyData.companyName,
        createdBy: 'Automated Onboarding',
        email: companyData.userCredential.user.email,
        firstName: companyData.userCredential.user?.displayName?.split(' ')[0] || '',
        industry: companyData.companyIndustry,
        lastName: companyData.userCredential.user?.displayName?.split(' ')[1] || '',
        ownerUID: companyData.userCredential.user.uid,
      }).catch((error) => {
        console.log(error);
        return reject({ status: error.code, message: error.message });
      });
      return await setDoc(
        doc(this.FBDatabase, 'users', companyData.userCredential.user.uid),
        {
          organisations: [createDoc.id],
          onboardingStage: 'completedInitialOnboarding',
        },
        { merge: true }
      )
        .then(async () => {
          localStorage.setItem('companyID', createDoc.id);
          return resolve(await this.callTenancyCreator(createDoc.id));
        })
        .catch((error) => {
          return reject({ status: error.code, message: error.message });
        });
    });
  }

  /**
   * The function `isValidABN` is an asynchronous function that checks if a given ABN (Australian
   * Business Number) is valid by applying the ATO (Australian Taxation Office) check method.
   * @param rawAbn - The `rawAbn` parameter is a string representing an Australian Business Number
   * (ABN).
   * @returns The function `isValidABN` is returning a Promise that resolves to a boolean value.
   */
  async isValidABN(rawAbn) {
    return new Promise(async (resolve, reject) => {
      if (!rawAbn) {
        return false;
      }

      // check if length is 11 digits
      if (rawAbn.length !== 11) {
        return false;
      }

      // apply ato check method
      let sum = 0;
      for (let position = 0; position < abnWeights.length; position += 1) {
        const weight = abnWeights[position];
        const digit = parseInt(rawAbn[position], 10) - (position ? 0 : 1);
        sum += weight * digit;
      }

      const checksum = sum % 89;

      return resolve(checksum === 0);
    });
  }

  /**
   * The function `isValidACN` checks if a given Australian Company Number (ACN) is valid by applying
   * the ATO check method.
   * @param rawAcn - The `rawAcn` parameter is a string representing the Australian Company Number
   * (ACN) that needs to be validated.
   * @returns The function `isValidACN` returns a Promise that resolves to a boolean value.
   */
  async isValidACN(rawAcn) {
    return new Promise(async (resolve, reject) => {
      if (!rawAcn) {
        return false;
      }

      // check if length is 9 digits
      if (rawAcn.length !== 9) {
        return false;
      }

      // apply ato check method
      let sum = 0;
      for (let position = 0; position < acnWeights.length; position += 1) {
        const weight = acnWeights[position];
        const digit = parseInt(rawAcn[position], 10);
        sum += weight * digit;
      }

      const checksum = (10 - (sum % 10)) % 10;

      return resolve(checksum === parseInt(rawAcn[8], 10));
    });
  }

  /**
   * The function `signUserOut` signs the user out and returns a promise that resolves with a success
   * message or rejects with an error message.
   * @returns a Promise.
   */
  async signUserOut() {
    return new Promise(async (resolve, reject) => {
      return await signOut(this.FBAuth)
        .then(async () => {
          // Clear Local Storage
          localStorage.clear();
          // Clear Session Storage
          sessionStorage.clear();
          // Clear IndexedDB
          let r = await window.indexedDB.databases();
          for (var i = 0; i < r.length; i++) window.indexedDB.deleteDatabase(r[i].name);
          // Clear all Cookies
          let cookies = document.cookie.split(';');
          for (let i = 0; i < cookies.length; i++) {
            let cookie = cookies[i];
            let eqPos = cookie.indexOf('=');
            let name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
          }
          // Clear Cache
          if ('caches' in window) {
            // Get all cache names
            caches.keys().then((cacheNames) => {
              // Iterate through each cache and delete it
              cacheNames.forEach((cacheName) => {
                caches.delete(cacheName);
              });
            });
          }
          // Clear SharedStorage
          if ('sharedStorage' in window) {
            window.sharedStorage.clear();
          }
          return resolve({ status: 201, message: 'User signed out successfully.' });
        })
        .catch((error) => {
          console.log(error);
          return reject({ status: error.code, message: error.message });
        });
    });
  }

  /**
   * The function `checkForDisposableEmail` uses the Abstract API to check if an email address is
   * disposable or not.
   * @param email - The `email` parameter is the email address that you want to check for disposable
   * email status.
   * @returns a Promise.
   */
  async checkForDisposableEmail(email) {
    return new Promise(async (resolve, reject) => {
      const url = `https://disposable.debounce.io/?email=${email}`;
      const xmlHttp = new XMLHttpRequest();
      xmlHttp.open('GET', url, true); // true for asynchronous
      xmlHttp.send(null);
      return (xmlHttp.onreadystatechange = function () {
        if (xmlHttp.readyState === 4 && xmlHttp.status === 200) {
          console.log(xmlHttp.responseText);
          console.log('isDisposableEmail = ' + JSON.parse(xmlHttp.responseText).disposable);
          if (JSON.parse(xmlHttp.responseText).disposable === 'true') {
            return resolve(true);
          } else if (JSON.parse(xmlHttp.responseText).disposable === 'false') {
            return resolve(false);
          }
        }
      });
    });
  }

  /**
   * The function `dataTypeOpenAICall` makes an asynchronous call to a Google AI function, passing in
   * parameters based on whether the function has been called previously or not, and stores the
   * response data in an array.
   * @param industry - The `industry` parameter is a variable that represents the industry for which
   * the data types are being generated. It is used as a parameter in the `googleAICall` function to
   * retrieve data types specific to that industry.
   * @returns a Promise.
   */
  async dataTypeOpenAICall(industry) {
    return new Promise(async (resolve, reject) => {
      const googleAICall = await httpsCallable(this.FBFunctions, 'googleaicall');
      if (previousDataTypes.length === 0) {
        // If this function hasn't been called previously, it will run a base function to generate the initial list of data types.
        return await googleAICall({
          callType: 'dataType',
          calledPreviously: false,
          industry: industry,
        })
          .then(async (response) => {
            console.log(response);
            for (let dataType of response.data) {
              previousDataTypes.push(dataType.toProperCase());
            }
            return resolve({
              status: 201,
              message: 'Data types generated successfully.',
              dataTypes: response.data,
            });
          })
          .catch((error) => {
            console.log(error);
            return reject({ status: error.code, message: error.message });
          });
      } else if (previousDataTypes.length !== 0) {
        // If this function has been called previously, it will run the base prompt and include the list of data types that have been suggested previously.
        return await googleAICall({
          callType: 'dataType',
          calledPreviously: true,
          industry: industry,
          previousDataTypes: previousDataTypes,
        })
          .then(async (response) => {
            console.log(response);
            for (let dataType of response.data) {
              previousDataTypes.push(dataType.toProperCase());
            }
            console.log(previousDataTypes);
            return resolve({
              status: 201,
              message: 'Data types generated successfully.',
              dataTypes: response.data,
            });
          })
          .catch((error) => {
            console.log(error);
            return reject({ status: error.code, message: error.message });
          });
      }
    });
  }

  /**
   * The function `productivityToolsOpenAICall` is an asynchronous function that generates a list of
   * productivity tools based on the industry parameter using Google AI, and stores the generated tools
   * and data storage locations in previousTools and dataStorageLocations respectively.
   * @param industry - The "industry" parameter is a variable that represents the industry for which
   * the productivity tools are being generated. It is used as a parameter in the Google AI call to
   * retrieve relevant productivity tools based on the specified industry.
   * @returns a Promise that resolves to an object with the following properties:
   * - status: The HTTP status code (201 in this case)
   * - message: A success message
   * - productivityTools: An array of productivity tools
   * - dataStorageLocationMap: A map of data storage locations
   */
  async productivityToolsOpenAICall(industry) {
    var dataStorageLocations = [];
    return new Promise(async (resolve, reject) => {
      const googleAICall = await httpsCallable(this.FBFunctions, 'googleaicall');
      if (previousTools.length === 0) {
        // If this function hasn't been called previously, it will run a base function to generate the initial list of data types.
        return await googleAICall({
          callType: 'productivityCall',
          calledPreviously: false,
          industry: industry,
        })
          .then(async (response) => {
            console.log(response);
            for (let dataType of response.data.toolsArray) {
              previousTools.push(dataType);
            }
            return resolve({
              status: 201,
              message: 'Data types generated successfully.',
              productivityTools: response.data.toolsArray,
            });
          })
          .catch((error) => {
            console.log(error);
            return reject({ status: error.code, message: error.message });
          });
      } else if (previousTools.length !== 0) {
        // If this function has been called previously, it will run the base prompt and include the list of data types that have been suggested previously.
        return await googleAICall({
          callType: 'productivityCall',
          calledPreviously: true,
          industry: industry,
          previousTools: previousTools,
        })
          .then(async (response) => {
            console.log(response);
            for (let dataType of response.data.toolsArray) {
              previousTools.push(dataType);
            }
            console.log(previousTools);
            return resolve({
              status: 201,
              message: 'Data types generated successfully.',
              productivityTools: response.data.toolsArray,
            });
          })
          .catch((error) => {
            console.log(error);
            return reject({ status: error.code, message: error.message });
          });
      }
    });
  }

  async salesToolsOpenAICall(industry) {
    var dataStorageLocations = [];
    return new Promise(async (resolve, reject) => {
      const googleAICall = await httpsCallable(this.FBFunctions, 'googleaicall');
      if (previousSalesTools.length === 0) {
        // If this function hasn't been called previously, it will run a base function to generate the initial list of data types.
        return await googleAICall({
          callType: 'salesCall',
          calledPreviously: false,
          industry: industry,
        })
          .then(async (response) => {
            console.log(response);
            for (let dataType of response.data.toolsArray) {
              previousSalesTools.push(dataType);
            }
            return resolve({
              status: 201,
              message: 'Sales software generated successfully.',
              productivityTools: response.data.toolsArray,
            });
          })
          .catch((error) => {
            console.log(error);
            return reject({ status: error.code, message: error.message });
          });
      } else if (previousSalesTools.length !== 0) {
        // If this function has been called previously, it will run the base prompt and include the list of data types that have been suggested previously.
        return await googleAICall({
          callType: 'salesCall',
          calledPreviously: true,
          industry: industry,
          previousTools: previousSalesTools,
        })
          .then(async (response) => {
            console.log(response);
            for (let dataType of response.data.toolsArray) {
              previousSalesTools.push(dataType);
            }
            console.log(previousSalesTools);
            return resolve({
              status: 201,
              message: 'Sales software generated successfully.',
              productivityTools: response.data.toolsArray,
            });
          })
          .catch((error) => {
            console.log(error);
            return reject({ status: error.code, message: error.message });
          });
      }
    });
  }

  async accountingToolsOpenAICall(industry) {
    var dataStorageLocations = [];
    return new Promise(async (resolve, reject) => {
      const googleAICall = await httpsCallable(this.FBFunctions, 'googleaicall');
      if (previousAccountingTools.length === 0) {
        // If this function hasn't been called previously, it will run a base function to generate the initial list of data types.
        return await googleAICall({
          callType: 'accountingCall',
          calledPreviously: false,
          industry: industry,
        })
          .then(async (response) => {
            console.log(response);
            for (let dataType of response.data.toolsArray) {
              previousAccountingTools.push(dataType);
            }
            return resolve({
              status: 201,
              message: 'Accounting software generated successfully.',
              productivityTools: response.data.toolsArray,
            });
          })
          .catch((error) => {
            console.log(error);
            return reject({ status: error.code, message: error.message });
          });
      } else if (previousAccountingTools.length !== 0) {
        // If this function has been called previously, it will run the base prompt and include the list of data types that have been suggested previously.
        return await googleAICall({
          callType: 'accountingCall',
          calledPreviously: true,
          industry: industry,
          previousTools: previousAccountingTools,
        })
          .then(async (response) => {
            console.log(response);
            for (let dataType of response.data.toolsArray) {
              previousAccountingTools.push(dataType);
            }
            console.log(previousTools);
            return resolve({
              status: 201,
              message: 'Accounting tools generated successfully.',
              productivityTools: response.data.toolsArray,
            });
          })
          .catch((error) => {
            console.log(error);
            return reject({ status: error.code, message: error.message });
          });
      }
    });
  }

  /**
   * The function `saveSuppliers` saves supplier data to a database, including generating a unique
   * supplier ID and making an API call to retrieve data storage locations.
   * @param suppliersData - The `suppliersData` parameter is an object that contains the data related
   * to the suppliers. It has the following properties:
   * @returns a Promise.
   */
  async saveSuppliers(suppliersData) {
    console.log(`[OnboardingCommon] (saveSuppliers) =>`, suppliersData);
    return new Promise(async (resolve, reject) => {
      let selectedTools = suppliersData.selectedTools;
      let companyID = suppliersData.companyID;
      let currentSupplier = suppliersData.currentSupplier;
      console.log(selectedTools);
      console.log(companyID);
      return await getDoc(
        doc(this.FBDatabase, 'customers', companyID, 'compliance', 'supplierRegister')
      )
        .then(async (supplierRegisterDoc) => {
          if (supplierRegisterDoc.exists()) {
            console.log('Supplier register exists');
            let suppliers = await supplierRegisterDoc.data().register.suppliers;
            let supplierID = 0;
            for (let supplierValue of Object.keys(suppliers)) {
              supplierID += 1;
              if (suppliers[supplierValue].supplierID !== supplierID) {
                supplierID = suppliers[supplierValue].supplierID;
              }
              console.log(supplierID);
            }
            let supplierJSON = {};
            for (let supplierKey = 0; supplierKey < selectedTools.length; supplierKey += 1) {
              let supplier = selectedTools[supplierKey].supplier;
              if (supplier === currentSupplier) {
                let dataTypesShared = selectedTools[supplierKey].dataTypes.filter(
                  (value, index, self) =>
                    self.findIndex((item) => item.toLowerCase() === value.toLowerCase()) === index
                );
                console.log(dataTypesShared);
                if (
                  (dataTypesShared.length !== 0 && !dataTypesShared.includes('None')) ||
                  (dataTypesShared.length !== 0 && !dataTypesShared.includes('Other'))
                ) {
                  supplierID += 1;
                  const googleAICall = httpsCallable(this.FBFunctions, 'googleaicall');
                  await googleAICall({
                    callType: 'dataStorageLocation',
                    companyName: supplier,
                  }).then(async (response) => {
                    let dataStorageLocation = await response.data;
                    return (supplierJSON[supplierID] = {
                      dataStorageLocations: dataStorageLocation,
                      dataTypesShared: dataTypesShared,
                      supplierContactEmail: '',
                      supplierContactName: '',
                      supplierID: supplierID,
                      supplierMetadata: {
                        addedBy: this.FBAuth.currentUser.uid,
                        dateAdded: new Date(),
                        dateLastUpdated: new Date(),
                      },
                      supplierName: supplier,
                      supplierWebsite: '',
                    });
                  });
                }
              }
            }
            console.log(supplierJSON);
            return await setDoc(
              doc(this.FBDatabase, 'customers', companyID, 'compliance', 'supplierRegister'),
              {
                register: {
                  suppliers: {
                    ...supplierJSON,
                  },
                },
              },
              { merge: true }
            )
              .then(() => {
                return resolve({ status: 201, message: 'Suppliers saved successfully.' });
              })
              .catch((err) => {
                console.log(err);
                return reject({ status: err.code, message: err.message });
              });
          } else {
            console.log('Does not exist');
            return reject({ status: 404, message: 'Suppliers not saved.' });
          }
        })
        .catch((error) => {
          console.log(error);
          return reject({ status: error.code, message: error.message });
        });
    });
  }

  /**
   * The function `loadRetentionSchedule` is an asynchronous function that retrieves the retention
   * schedule for a given company ID from a Firebase database and returns an array of data types.
   * @param companyID - The `companyID` parameter is the unique identifier for a specific company in the
   * database. It is used to locate the relevant data for that company's retention schedule.
   * @returns The loadRetentionSchedule function returns a Promise.
   */
  async loadRetentionSchedule(companyID) {
    return new Promise(async (resolve, reject) => {
      return getDoc(doc(this.FBDatabase, 'customers', companyID, 'compliance', 'retentionSchedule'))
        .then(async (retentionScheduleDoc) => {
          let retentionSchedule = Object.keys(await retentionScheduleDoc.data().register.dataTypes);
          let dataTypesArray = [];
          for (const key of retentionSchedule) {
            dataTypesArray.push(retentionScheduleDoc.data().register.dataTypes[key].dataTypeName);
          }
          return resolve(dataTypesArray);
        })
        .catch((error) => {
          console.log(error);
          return reject({ status: error.code, message: error.message });
        });
    });
  }

  /**
   * The function `dataStorageOpenAICall` makes an asynchronous call to a Google AI function to
   * retrieve the data storage location for a given company name.
   * @param companyName - The `companyName` parameter is a string that represents the name of the
   * company for which you want to make a data storage location call.
   */
  async dataStorageOpenAICall(companyName) {
    new Promise(async (resolve, reject) => {
      const googleAICall = httpsCallable(this.FBFunctions, 'googleaicall');
      return await googleAICall({ callType: 'dataStorageLocation', companyName: companyName })
        .then((response) => {
          console.log(response);
          return resolve({
            status: 201,
            message: 'Data storage location generated successfully.',
            dataStorageLocation: response.data,
          });
        })
        .catch((error) => {
          console.log(error);
          return reject({ status: error.code, message: error.message });
        });
    });
  }

  /**
   * The function `mxRecordsRecon` makes an asynchronous request to a Firebase function called
   * `mxrecordsrequest` and returns the email provider if the response is successful.
   * @param companyDomain - The companyDomain parameter is a string that represents the domain name of
   * a company.
   * @returns the email provider for the given company domain.
   */
  async mxRecordsRecon(email) {
    return new Promise(async (resolve, reject) => {
      let companyDomain = email.split('@')[1];
      const mxrecordsrequest = await httpsCallable(this.FBFunctions, 'mxrecordsrequest');
      return await mxrecordsrequest({ companyDomain: companyDomain })
        .then((response) => {
          console.log(response);
          if (
            response.data.statusCode === 200 ||
            (response.data.statusCode === 201 &&
              response.data.emailProvider !== null &&
              response.data.emailProvider !== undefined &&
              response.data.emailProvider !== '')
          ) {
            return resolve({
              status: 201,
              mesage: 'Email provider found successfully.',
              emailProvider: response.data.emailProvider,
            });
          }
        })
        .catch((error) => {
          return reject({ status: error.code, message: error.message });
        });
    });
  }

  /**
   * The function `stripeCheckoutCall()` initiates a Stripe checkout session for a user, updates their
   * onboarding stage in the Firebase database, and redirects them to the Stripe checkout URL.
   * @returns The `stripeCheckoutCall` function returns a Promise.
   */
  async stripeCheckoutCall() {
    return new Promise(async (resolve, reject) => {
      return await setDoc(
        doc(this.FBDatabase, 'users', this.FBAuth.currentUser.uid),
        {
          onboardingStage: 'paymentStarted',
        },
        { merge: true }
      )
        .then(async () => {
          let stripeDoc = await addDoc(
            collection(
              this.FBDatabase,
              'stripe-customer-details',
              this.FBAuth.currentUser.uid,
              'checkout_sessions'
            ),
            {
              automatic_tax: true,
              line_items: [
                {
                  price: 'price_1NfC9QFymOjWe2SiqclezlYh',
                  quantity: '1',
                },
              ],
              trial_from_plan: true,
              after_expiration: {
                recovery: {
                  enabled: true,
                },
              },
              tax_id_collection: {
                enabled: true,
              },
              allow_promotion_codes: true,
              subscription_data: {
                trial_settings: {
                  end_behavior: {
                    missing_payment_method: 'pause',
                  },
                },
              },
              success_url: window.location.href,
              cancel_url: window.location.href,
            }
          );
          return onSnapshot(
            doc(
              this.FBDatabase,
              'stripe-customer-details',
              this.FBAuth.currentUser.uid,
              'checkout_sessions',
              stripeDoc.id
            ),
            async (snap) => {
              const { error, url } = snap.data();
              if (error) {
                // Show an error to your customer and
                // inspect your Cloud Function logs in the Firebase console.
                alert(`An error occured: ${error.message}`);
                reject({ status: error.code, message: error.message });
              }
              if (url) {
                // We have a Stripe Checkout URL, let's redirect.
                console.log('Stripe Checkout URL created successfully.');
                return resolve(window.location.assign(url));
              }
            }
          );
        })
        .catch((error) => {
          console.log(error);
          return reject({ status: error.code, message: error.message });
        });
    });
  }

  /**
   * The function `checkStripeCollection` checks if a user has an active Stripe subscription and
   * updates their onboarding stage accordingly.
   * @returns a Promise.
   */
  async checkStripeCollection() {
    console.log('Starting stripe collection');
    return new Promise(async (resolve, reject) => {
      console.log('Running promise stripe collection');
      return await getDocs(
        collection(
          this.FBDatabase,
          'stripe-customer-details',
          this.FBAuth.currentUser.uid,
          'subscriptions'
        )
      ).then(async (snap) => {
        if (snap.docs.length !== 0) {
          console.log(snap.docs[0].data());
          let docData = snap.docs[0].data();
          if (docData.status === 'active') {
            console.log('Stripe subscription is active.');
            return await setDoc(
              doc(this.FBDatabase, 'users', this.FBAuth.currentUser.uid),
              {
                onboardingStage: 'paymentCompleted',
              },
              { merge: true }
            )
              .then(async () => {
                return resolve({
                  status: 201,
                  message: 'Stripe subscription created successfully.',
                });
              })
              .catch((error) => {
                console.log(error);
                return reject({ status: error.code, message: error.message });
              });
          }
        } else {
          return reject({ status: 500, message: 'No Stripe subscription found.' });
        }
      });
    });
  }

  /**
   * The function `checkOnboardingStage` checks the onboarding stage of a user and returns the
   * corresponding status, message, and stage.
   * @returns The function `checkOnboardingStage()` returns a Promise.
   */
  async checkOnboardingStage() {
    console.log('Checking onboarding stage for user with ID: ' + this.FBAuth.currentUser.uid);
    return new Promise(async (resolve, reject) => {
      console.log('Running Promise');
      return await getDoc(doc(this.FBDatabase, 'users', this.FBAuth.currentUser.uid))
        .then(async (snap) => {
          console.log(snap.data());
          if (snap.data().onboardingStage !== undefined) {
            if (snap.data().onboardingStage === 'createAccount') {
              return resolve({
                status: 201,
                message: 'Onboarding stage set to create account.',
                stage: 'choose_standard',
              });
            } else if (snap.data().onboardingStage === 'paymentStarted') {
              return resolve({
                status: 201,
                message: 'Onboarding stage set to payment started.',
                stage: 'paymentStarted',
              });
            } else if (snap.data().onboardingStage === 'paymentCompleted') {
              return resolve({
                status: 201,
                message: 'Onboarding stage set to payment completed.',
                stage: 'paymentCompleted',
              });
            } else if (snap.data().onboardingStage === 'completedInitialOnboarding') {
              // window.location.assign('https://login.deiterate.com');
              return resolve({
                status: 201,
                message: 'Onboarding stage set to completed initial onboarding.',
                stage: 'completedInitialOnboarding',
              });
            }
          } else {
            return resolve({
              status: 201,
              message: 'Onboarding stage not set.',
              stage: 'createAccount',
            });
          }
        })
        .catch((error) => {
          console.log(error);
          return reject({ status: error.code, message: error.message, stage: 'createAccount' });
        });
    });
  }

  /**
   * The function "callTenancyCreator" makes an asynchronous call to a Firebase Cloud Function called
   * "paautomatedonboarding" and passes the companyID as a parameter.
   * @param companyID - The companyID parameter is the unique identifier for a company. It is used to
   * specify which company the tenancy creator should be called for.
   */
  async callTenancyCreator(companyID) {
    return new Promise(async (resolve, reject) => {
      const tenancyCreator = await httpsCallable(this.FBFunctions, 'paautomatedonboarding');
      return tenancyCreator({ companyID: companyID })
        .then((response) => {
          console.log(response);
          return resolve({ status: 201, message: 'Tenancy creator created successfully.' });
        })
        .catch((error) => {
          return reject({ status: error.code, message: error.message });
        });
    });
  }

  /**
   * The function checks if a user is signed in and returns a status and message accordingly.
   * @returns a Promise.
   */
  async checkForSignedInUser() {
    return new Promise(async (resolve, reject) => {
      let user = this.FBAuth.currentUser;
      console.log(user);
      if (user) {
        return await getDoc(doc(this.FBDatabase, 'users', user.uid)).then((usersDoc) => {
          if (usersDoc.exists()) {
            if (usersDoc.data()?.onboardingStage !== undefined) {
              return resolve({ status: 201, message: 'You are logged in.' });
            } else {
              this.FBAuth.signOut();
              return resolve({ status: 201, message: 'You are not logged in.' });
            }
          }
        });
      } else {
        return reject({ status: 500, message: 'You are not logged in.' });
      }
    });
  }

  /**
   * The function `saveDataTypes` saves an array of data types to a database, along with some
   * additional information, and returns a promise that resolves with a success message or rejects with
   * an error message.
   * @param dataTypeData - The `dataTypeData` parameter is an object that contains the following
   * properties:
   * @returns The function `saveDataTypes` returns a Promise.
   */
  async saveDataTypes(dataTypeData) {
    return new Promise(async (resolve, reject) => {
      let dataTypesArray = dataTypeData.dataTypesSelectedArray;
      let companyID = dataTypeData.companyID;
      let dataTypesJSON = {};

      return getDoc(doc(this.FBDatabase, 'customers', companyID, 'compliance', 'retentionSchedule'))
        .then(async (retentionScheduleDoc) => {
          if (retentionScheduleDoc.exists()) {
            const dataTypes = await retentionScheduleDoc.data().register.dataTypes;
            let externalDataTypeCount = 0;
            for (const dataType of Object.keys(dataTypes)) {
              externalDataTypeCount += 1;
              if (dataTypes[dataType].dataTypeID !== externalDataTypeCount) {
                externalDataTypeCount = dataTypes[dataType].dataTypeID;
              }
              console.log(externalDataTypeCount);
            }
            let dataTypeID = externalDataTypeCount;
            for (let dataType of dataTypesArray) {
              dataTypeID += 1;
              dataTypesJSON[dataTypeID] = {
                dataRetentionPeriod: {
                  max: 120,
                  min: 0,
                },
                dataTypeID: dataTypeID,
                dataTypeName: dataType,
                dataTypeJustification: '',
                dataTypeMetadata: {
                  addedBy: this.FBAuth.currentUser.uid,
                  dateAdded: new Date(),
                  dateLastUpdated: new Date(),
                },
                deleted: false,
              };
            }
            return await setDoc(
              doc(this.FBDatabase, 'customers', companyID, 'compliance', 'retentionSchedule'),
              {
                register: {
                  dataTypes: dataTypesJSON,
                },
              },
              { merge: true }
            )
              .then(() => {
                return resolve({ status: 201, message: 'Data types saved successfully.' });
              })
              .catch((err) => {
                console.log(err);
                return reject({ status: err.code, message: err.message });
              });
          } else {
            let dataTypeID = 0;
            for (let dataType of dataTypesArray) {
              dataTypeID += 1;
              dataTypesJSON[dataTypeID] = {
                dataRetentionPeriod: {
                  max: 120,
                  min: 0,
                },
                dataTypeID: dataTypeID,
                dataTypeName: dataType,
                dataTypeJustification: '',
                dataTypeMetadata: {
                  addedBy: this.FBAuth.currentUser.uid,
                  dateAdded: new Date(),
                  dateLastUpdated: new Date(),
                },
                deleted: false,
              };
            }
            return await setDoc(
              doc(this.FBDatabase, 'customers', companyID, 'compliance', 'retentionSchedule'),
              {
                register: {
                  dataTypes: dataTypesJSON,
                },
              },
              { merge: true }
            )
              .then(() => {
                return resolve({ status: 201, message: 'Data types saved successfully.' });
              })
              .catch((err) => {
                console.log(err);
                return reject({ status: err.code, message: err.message });
              });
          }
        })
        .catch((error) => {
          console.log(error);
          return reject({ status: error.code, message: error.message });
        });
    });
  }

  /**
   * The function `saveRetentionSchedule` saves a retention schedule for data types in a database.
   * @param dataTypeData - The `dataTypeData` parameter is an object that contains the following
   * properties:
   * @returns a Promise.
   */
  async saveRetentionSchedule(dataTypeData) {
    return new Promise(async (resolve, reject) => {
      let dataTypesMap = dataTypeData.dataTypesMap;
      let companyID = dataTypeData.companyID;

      return getDoc(doc(this.FBDatabase, 'customers', companyID, 'compliance', 'retentionSchedule'))
        .then(async (retentionScheduleDoc) => {
          if (retentionScheduleDoc.exists()) {
            var dataTypesJSON = {};
            let dataTypes = await retentionScheduleDoc.data().register.dataTypes;
            for (const dataTypeValue of Object.keys(dataTypesMap)) {
              for (const dataType of Object.keys(dataTypes)) {
                console.log(dataType);
                let dataRetentionPeriod = dataTypesMap[dataTypeValue].dataRetentionPeriod;
                let dataTypeName = dataTypesMap[dataTypeValue].dataTypeName;
                let minRetentionPeriod = dataRetentionPeriod.min;
                let maxRetentionPeriod = dataRetentionPeriod.max;
                if (dataTypes[dataType].dataTypeName === dataTypeName) {
                  console.log(true);
                  dataTypesJSON[dataType] = {
                    dataRetentionPeriod: {
                      max: maxRetentionPeriod,
                      min: minRetentionPeriod,
                    },
                    dataTypeID: dataType,
                    dataTypeName: dataTypeName,
                    dataTypeJustification: '',
                    dataTypeMetadata: {
                      addedBy: this.FBAuth.currentUser.uid,
                      dateAdded: new Date(),
                      dateLastUpdated: new Date(),
                    },
                    deleted: false,
                  };
                }
              }
            }
            console.log(dataTypesJSON);
            return await setDoc(
              doc(this.FBDatabase, 'customers', companyID, 'compliance', 'retentionSchedule'),
              {
                register: {
                  dataTypes: dataTypesJSON,
                },
              },
              { merge: true }
            )
              .then(() => {
                return resolve({ status: 201, message: 'Retention schedule saved successfully.' });
              })
              .catch((err) => {
                console.log(err);
                return reject({ status: err.code, message: err.message });
              });
          }
        })
        .catch((error) => {
          console.log(error);
          return reject({ status: error.code, message: error.message });
        });
    });
  }

  async loadMSPs() {
    return new Promise(async (resolve, reject) => {
      return getDoc(doc(this.FBDatabase, 'mspDetails', 'availableMSPs')).then(async (doc) => {
        if (doc.exists()) {
          let availableMSPs = await doc.data().availableMSPs;
          let mspsArray = [];
          for (const msp of Object.keys(availableMSPs)) {
            if (availableMSPs[msp]?.customerID !== undefined) {
              mspsArray.push({
                mspName: availableMSPs[msp].mspName,
                mspID: availableMSPs[msp].customerID,
              });
            }
          }
          return resolve(mspsArray);
        }
      });
    });
  }

  async setMSP(companyID, mspID) {
    return new Promise(async (resolve, reject) => {
      return await setDoc(
        doc(this.FBDatabase, 'customers', companyID),
        {
          mspDetails: {
            techPartner: {
              impersonationAllowed: true,
              msp: mspID,
            },
          },
        },
        { merge: true }
      ).then(async () => {
        this.mspReassign(companyID, mspID);
        const append = httpsCallable(this.FBFunctions, "appendtomsporgs");
        await append({
          companyID: companyID,
          mspID: mspID,
        }).then(() => {
          return resolve({ status: 201, message: 'MSP successfully set.' });
        })
      });
    });
  }
  
  async mspReassign (companyID, mspID) {
    return new Promise(async (resolve, reject) => {
      
      const functionName = 'mspreassign'; // Replace with your function name
      const reassign = httpsCallable(this.FBFunctions, functionName);

      return await reassign({
        customerID: companyID,
        mspID: mspID,
      })
        .then(async (result) => {
          console.log('Response:', result.data);
          return resolve({ status: 201, message: 'E8 Tasks successfully reassigned.' });
        })
        .catch((error) => {
          console.error('Error:', error);
          return reject({ status: error.code, message: error.message });
        });
    });
  }


  async updateOnboardingMap(key, value, companyID = localStorage.getItem('companyID')) {
    return new Promise(async (resolve, reject) => {
      if (key === '' || key === undefined || key === null) {
        return reject({ status: 500, message: 'Key not provided.' });
      }

      if (value === '' || value === undefined || value === null) {
        return reject({ status: 500, message: 'Value not provided.' });
      }

      return await setDoc(
        doc(this.FBDatabase, 'customers', companyID),
        {
          onboardingMap: {
            [key]: value,
          },
        },
        { merge: true }
      )
        .then((res) => {
          console.log(res);
          return resolve({ status: 201, message: 'Onboarding map updated successfully.' });
        })
        .catch((error) => {
          return reject({ status: error.code, message: error.message });
        });
    });
  }

  async getCompanyData() {
    return new Promise(async (resolve, reject) => {
      await getDoc(doc(this.FBDatabase, 'users', this.FBAuth.currentUser.uid)).then(
        async (usersDoc) => {
          if (usersDoc.exists()) {
            let userData = await usersDoc.data();
            let companyID = userData.organisations[0];
            localStorage.setItem('companyID', companyID);

            await getDoc(doc(this.FBDatabase, 'pa_automated_onboarding', companyID)).then(
              async (customersDoc) => {
                if (customersDoc.exists()) {
                  console.log(JSON.stringify(customersDoc.data()));
                  return resolve(JSON.stringify(customersDoc.data()));
                }
              }
            );
          }
        }
      );
    });
  }

  async sendSignupEmail(data) {
    return new Promise(async (resolve, reject) => {
      const sendSignUpNotification = httpsCallable(this.FBFunctions, 'sendsignupnotification');

      if (data === undefined || data === null) {
        return reject({ status: 500, message: 'Data not provided.' });
      }

      if (data.ownerName === undefined || data.ownerName === null || data.ownerName === '') {
        return reject({ status: 500, message: 'Owner name not provided.' });
      }

      if (data.ownerEmail === undefined || data.ownerEmail === null || data.ownerEmail === '') {
        return reject({ status: 500, message: 'Owner email not provided.' });
      }

      if (
        data.ownerPhoneNumber === undefined ||
        data.ownerPhoneNumber === null ||
        data.ownerPhoneNumber === ''
      ) {
        return reject({ status: 500, message: 'Owner phone number not provided.' });
      }

      sendSignUpNotification({
        ownerName: data.ownerName,
        ownerEmail: data.ownerEmail,
        ownerPhoneNumber: data.ownerPhoneNumber,
      })
        .then((result) => {
          console.log(result);
          return resolve({ status: 201, message: 'Signup email sent successfully.' });
        })
        .catch((error) => {
          console.log(error);
          return reject({ status: error.code, message: error.message });
        });
    });
  }

  async sendStripeNotification(data) {
    return new Promise(async (resolve, reject) => {
      const sendStripeNotificationFunction = httpsCallable(
        this.FBFunctions,
        'sendstripenotification'
      );

      if (data === undefined || data === null) {
        return reject({ status: 500, message: 'Data not provided.' });
      }

      if (data.companyName === undefined || data.companyName === null || data.companyName === '') {
        return reject({ status: 500, message: 'Company name not provided.' });
      }

      if (data.ownerName === undefined || data.ownerName === null || data.ownerName === '') {
        return reject({ status: 500, message: 'Owner name not provided.' });
      }

      if (data.ownerEmail === undefined || data.ownerEmail === null || data.ownerEmail === '') {
        return reject({ status: 500, message: 'Owner email not provided.' });
      }

      if (
        data.ownerPhoneNumber === undefined ||
        data.ownerPhoneNumber === null ||
        data.ownerPhoneNumber === ''
      ) {
        return reject({ status: 500, message: 'Owner phone number not provided.' });
      }

      sendStripeNotificationFunction({
        companyName: data.companyName,
        ownerName: data.ownerName,
        ownerEmail: data.ownerEmail,
        ownerPhoneNumber: data.ownerPhoneNumber,
      })
        .then((result) => {
          console.log(result);
          return resolve({ status: 201, message: 'Stripe notification sent successfully.' });
        })
        .catch((error) => {
          console.log(error);
          return reject({ status: error.code, message: error.message });
        });
    });
  }

  async configureWelcomeAutomation(data) {
    return new Promise(async (resolve, reject) => {
      const addToWelcomeAutomation = httpsCallable(this.FBFunctions, 'addtowelcomeautomation');

      if (data === undefined || data === null) {
        return reject({ status: 500, message: 'Data not provided.' });
      }

      if (data.firstName === undefined || data.firstName === null || data.firstName === '') {
        return reject({ status: 500, message: 'First name not provided.' });
      }

      if (data.email === undefined || data.email === null || data.email === '') {
        return reject({ status: 500, message: 'Email not provided.' });
      }

      addToWelcomeAutomation({
        firstName: data.firstName,
        email: data.email,
      })
        .then((result) => {
          console.log(result);
          return resolve({ status: 201, message: 'Configured welcome automation succesfully.' });
        })
        .catch((error) => {
          console.log(error);
          return reject({ status: error.code, message: error.message });
        });
    });
  }

  async getUserDetails() {
    let user = this.FBAuth.currentUser;
    return new Promise(async (resolve, reject) => {
      if (user) {
        return await getDoc(doc(this.FBDatabase, 'users', user.uid)).then((usersDoc) => {
          if (usersDoc.exists()) {
            return resolve(usersDoc.data());
          }
        });
      } else {
        return reject({ status: 500, message: 'You are not logged in.' });
      }
    });
  }

  /**
   * The function `consoleWarning()` displays a warning message in the console, informing users not to
   * paste anything in the console as it could be a security risk.
   */
  consoleWarning() {
    // Styling for the console message
    const consoleStyle = `
      background: linear-gradient(#FF6161, #FF3838);
      border: 1px solid #FF3838;
      color: white;
      font-size: 20px;
      padding: 10px;
      font-weight: bold;
    `;

    // The warning message
    const warningMessage = [
      `%c⚠️ WARNING: Do not paste anything in the console! ⚠️`,
      `Pasting code here could allow attackers to access your account and personal information.`,
      `If someone asks you to paste anything here, do not do it!`,
      `Only enter code that you trust and understand.`,
      `This is a browser feature intended for developers. If someone asks you to do anything here, it could be a scam!%c`,
    ].join('\n');

    // Display the warning in the console
    console.warn(warningMessage, consoleStyle, consoleStyle);

    if (this.isDebug || this.demo) {
      console.warn(
        `%cTo Disable this message in the future set to "debug: true" and "demo: false" to the "DeiterateCommon" class in "DeiterateCommon.js"%c`,
        consoleStyle,
        consoleStyle
      );
    }
  }

  /**
   * The function `getCapitalised` takes a string as input and returns the same string with the first
   * letter of each word capitalized, except for certain small words.
   * @param str - The `str` parameter is a string that represents the input text.
   * @returns The function `getCapitalised` returns a string with the first letter of each word
   * capitalized, except for certain small words that are not capitalized.
   */
  getCapitalised(str) {
    var smallWords = /^(a|an|and|as|at|but|by|en|for|if|in|nor|of|on|or|per|the|to|vs?\.?|via)$/i;
    return str.replace(/[A-Za-z0-9\u00C0-\u00FF]+[^\s-']*/g, function (match, index, title) {
      if (
        index > 0 &&
        index + match.length !== title.length &&
        match.search(smallWords) > -1 &&
        title.charAt(index - 2) !== ':' &&
        (title.charAt(index + match.length) !== '-' || title.charAt(index - 1) === '-') &&
        (title.charAt(index + match.length) !== "'" || title.charAt(index - 1) === "'") &&
        title.charAt(index - 1).search(/[^\s-]/) < 0
      ) {
        return match.toLowerCase();
      }
      if (match.substr(1).search(/[A-Z]|\../) > -1) {
        return match;
      }
      return match.charAt(0).toUpperCase() + match.substr(1);
    });
  }

  boxesChecked() {
    // Check if checkboxes are checked before continuing to next screen
    const checkboxes = document.querySelectorAll('.page-current input[type="checkbox"]');

    // loop through checkboxes and if all are checked - change page
    let allChecked = true;
    for (let i = 0; i < checkboxes.length; i++) {
      if (!checkboxes[i].checked) {
        allChecked = false;
      }
    }

    return allChecked;
  }

  inputsFilled() {
    let allFilled = true;
    let inputs = document.querySelectorAll('.page-current input');
    inputs.forEach((input) => {
      if (input.value == '') {
        allFilled = false;
      }
      // if (this.isEmpty(input.value)) {
      //   allFilled = false;
      // }
    });
    return allFilled;
  }

  processString = (str) => {
    return this.getCapitalised(
      str
        .replace(/-/gi, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim()
    );
  };

  allInOneValidation(validateInputs = true, validateCheckBoxes = true, validateTextAreas = true) {
    var inputs = [];
    var textareas = [];
    var checkboxes = [];
    var allInputs = [];
    var emptyInputs = [];

    if (validateInputs) {
      inputs = document.querySelectorAll('.page-current input');
    }

    if (validateTextAreas) {
      textareas = document.querySelectorAll('.page-current textarea');
    }

    if (validateCheckBoxes) {
      checkboxes = document.querySelectorAll('.page-current input[type="checkbox"]');
    }

    allInputs = [...inputs, ...textareas, ...checkboxes];

    for (let i = 0; i < allInputs.length; i++) {
      const input = allInputs[i];
      if (input.value == '' || !input.checked) {
        emptyInputs.push(this.processString(input.name));
      }
    }

    window.emptyInputs = emptyInputs;

    console.log('Empty inputs: ', emptyInputs);

    if (emptyInputs.length >= 1) {
      if (emptyInputs.length == 1) {
        window.f7.dialog.alert('Please fill in the ' + emptyInputs[0] + ' field.');
      } else {
        window.f7.dialog.alert('Please fill in the ' + emptyInputs.join(', ') + ' fields.');
      }
      return false;
    } else return true;
  }

  focusInputs() {
    let focusAllInputs = document.querySelectorAll('.page-current input');
    for (let i = 0; i < focusAllInputs.length; i++) {
      focusAllInputs[i].focus();
    }

    for (let i = 0; i < focusAllInputs.length; i++) {
      focusAllInputs[i].blur();
    }
  }

  // Check if string is not empty or null or space or other types of empty characters
  isEmpty(string) {
    // Array of known blank characters
    const blankRegex = /[\s\u200B-\u200D\u2028\u2029\uFEFF]/;
    if (string !== '' && string !== null && string !== undefined && string !== ' ') {
      return false;
    } else {
      return true;
    }
  }

  createDialog(title, text, buttons, type = 'alert', verticalButtons = false) {
    let dialog = window.f7.dialog.create({
      title: title,
      text: text,
      buttons: buttons,
      verticalButtons: verticalButtons,
    });

    if (type === 'alert') {
      dialog.open();
    }

    if (type === 'confirm') {
      dialog.confirm();
    }

    if (type === 'prompt') {
      dialog.prompt();
    }

    return dialog;
  }
}